import React, { useState, useEffect } from 'react';
import { DateTime } from 'luxon';
import Icon from '@mdi/react';
import { mdiClose, mdiCalendarSync, mdiHomeAccount, mdiArrowRight } from '@mdi/js';
import { toast } from 'react-toastify';
import { availabilityApi } from '../api/availability';
import DateInputWithPicker from './DateInputWithPicker';
import CustomFormInput from './forms/CustomFormInput';
import WeeklyDayRows, { DAY_NAMES, makeDay, buildWindows, computeDayGlyphItems } from './WeeklyDayRows';
import './WeeklyScheduleModal.css';

/**
 * WeeklyScheduleModal - Create recurring weekly availability schedules
 *
 * Publishes recurring patterns to recurring_availability collection.
 * Users can name schedules for easier management (e.g., "Term 1 Spring 2026").
 */
const WeeklyScheduleModal = ({ isOpen, defaultLocationName, onConfirm, onCancel }) => {
    // Schedule naming with generated default
    const [scheduleName, setScheduleName] = useState('');
    const generatedDefaultName = `Schedule - ${DateTime.local().toFormat('MMM d, yyyy')}`;

    // Per-day availability — Google-calendar style, one row per day (see
    // WeeklyDayRows for the shape and all group/break editing).
    const initialDayConfig = () => ({
        Sunday: makeDay(false),
        Monday: makeDay(true),
        Tuesday: makeDay(true),
        Wednesday: makeDay(true),
        Thursday: makeDay(true),
        Friday: makeDay(true),
        Saturday: makeDay(false),
    });
    const [dayConfig, setDayConfig] = useState(initialDayConfig);

    // Date range — endDate is the single source of truth; numWeeks stays in sync.
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [numWeeks, setNumWeeks] = useState(8);

    // Weeks between two ISO dates, rounded to the nearest whole week (min 1).
    const weeksBetween = (fromISO, toISO) => {
        const days = DateTime.fromISO(toISO).diff(DateTime.fromISO(fromISO), 'days').days;
        return Math.max(1, Math.round(days / 7));
    };

    // Editing duration → recompute end date from start + weeks.
    const handleWeeksChange = (weeks) => {
        const safeWeeks = Math.max(1, weeks || 1);
        setNumWeeks(safeWeeks);
        if (startDate) {
            setEndDate(DateTime.fromISO(startDate).plus({ weeks: safeWeeks }).toISODate());
        }
    };

    // Editing end date → recompute duration (rounded to nearest week).
    const handleEndDateChange = (newEndDate) => {
        setEndDate(newEndDate);
        if (startDate && newEndDate) {
            setNumWeeks(weeksBetween(startDate, newEndDate));
        }
    };

    // Editing start date → keep end date anchored to the current duration.
    const handleStartDateChange = (newStartDate) => {
        setStartDate(newStartDate);
        if (newStartDate && numWeeks) {
            setEndDate(DateTime.fromISO(newStartDate).plus({ weeks: numWeeks }).toISODate());
        }
    };

    const computedEndDate = endDate || null;

    // Lesson duration (minutes per bookable slot)
    const [duration, setDuration] = useState(60);

    // UI state
    const [isCreating, setIsCreating] = useState(false);
    const [affectedDates, setAffectedDates] = useState([]);

    // Calculate affected dates for preview
    useEffect(() => {
        const selectedDayNames = DAY_NAMES.filter(day => dayConfig[day].enabled);
        if (selectedDayNames.length === 0 || !startDate || !computedEndDate) {
            setAffectedDates([]);
            return;
        }

        const start = DateTime.fromISO(startDate);
        const end = DateTime.fromISO(computedEndDate);
        if (!start.isValid || !end.isValid || start > end) return;

        const dates = [];
        const dayIndexes = selectedDayNames.map(day =>
            ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].indexOf(day)
        );

        let current = start;
        while (current <= end) {
            const dayIndex = current.weekday === 7 ? 0 : current.weekday; // Luxon: 1=Mon, 7=Sun → 0=Sun
            if (dayIndexes.includes(dayIndex)) {
                dates.push(current);
            }
            current = current.plus({ days: 1 });
        }

        setAffectedDates(dates);
    }, [dayConfig, startDate, computedEndDate]);

    // Handle publish — serializes the per-day groups + breaks to windows[] and
    // creates the schedule via POST /recurring (the windows-based endpoint).
    // The old publish-schedule path flattened everything to one global
    // start/end for all days, silently discarding per-day config.
    const handlePublish = async () => {
        // Validation
        const selectedDayNames = DAY_NAMES.filter(day => dayConfig[day].enabled);
        if (selectedDayNames.length === 0) {
            toast.error('Please select at least one day');
            return;
        }

        if (!startDate) {
            toast.error('Please select a start date');
            return;
        }

        if (!computedEndDate) {
            toast.error('Please set a duration or end date');
            return;
        }

        const windows = buildWindows(dayConfig);
        if (windows.length === 0) {
            toast.error('Your breaks cover all the available time. Adjust the times or remove a break.');
            return;
        }

        setIsCreating(true);

        try {
            await availabilityApi.createRecurringSchedule({
                scheduleName: scheduleName.trim() || generatedDefaultName,
                timezone: 'Australia/Sydney',
                validFrom: startDate,
                validUntil: computedEndDate,
                defaultSlotDurationMins: duration,
                windows,
                // Breaks are baked into windows[] as gaps — the legacy
                // schedule-wide lunchBreak field stays disabled.
                lunchBreak: { enabled: false },
            });

            toast.success(`Published schedule "${scheduleName.trim() || generatedDefaultName}"`);
            onConfirm();
        } catch (error) {
            console.error('Error publishing schedule:', error);
            toast.error(error.message || 'Failed to publish schedule');
        } finally {
            setIsCreating(false);
        }
    };

    // Reset form when modal opens
    useEffect(() => {
        if (isOpen) {
            setScheduleName('');
            setDayConfig(initialDayConfig());
            setStartDate('');
            setEndDate('');
            setNumWeeks(8);
            setDuration(60);
        }
    }, [isOpen]);

    if (!isOpen) return null;

    return (
        <div className="weekly-schedule-modal-overlay" onClick={onCancel}>
            <div className="weekly-schedule-modal" onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className="weekly-schedule-modal-header">
                    <div className="weekly-schedule-modal-header-content">
                        <Icon path={mdiCalendarSync} size={1.2} color="var(--dl-color-turquoise-default)" />
                        <h2>Set Weekly Schedule</h2>
                    </div>
                    <button className="weekly-schedule-modal-close" onClick={onCancel}>
                        <Icon path={mdiClose} size={1} />
                    </button>
                </div>

                {/* Primary studio context strip — full-width row below the header,
                    mirroring the travel modals' .travel-context-banner (pill on a
                    gray strip). Home = turquoise pill. */}
                <div className="weekly-context-banner">
                    <div className="weekly-studio-pill">
                        <Icon path={mdiHomeAccount} size={0.7} color="var(--dl-color-turquoise-default)" />
                        <span>{defaultLocationName || 'Primary Studio'}</span>
                    </div>
                    <div className="weekly-studio-hint">
                        {defaultLocationName
                            ? 'Bookings on this schedule use your default location.'
                            : 'No default studio set yet, so bookings show as location TBA.'}
                    </div>
                </div>

                {/* Body */}
                <div className="weekly-schedule-modal-body">
                    {/* Schedule Name */}
                    <div className="form-group">
                        <label htmlFor="scheduleName" className="form-label">Schedule Name (Optional)</label>
                        <p className="form-hint">Leave blank to use: "{generatedDefaultName}"</p>
                        <input
                            type="text"
                            id="scheduleName"
                            className="form-input"
                            value={scheduleName}
                            onChange={(e) => setScheduleName(e.target.value)}
                            placeholder={generatedDefaultName}
                            maxLength={100}
                        />
                    </div>

                    {/* Lesson Duration — sits above General availability so the
                        instructor picks appointment length before setting hours.
                        Label + pills share one row (weekly-lesson-duration). */}
                    <div className="form-group weekly-lesson-duration">
                        <label className="form-label">Lesson Duration</label>
                        <div className="duration-radio">
                            <CustomFormInput
                                type="radio"
                                variant="turquoise"
                                layout="pill"
                                name="duration"
                                value="45"
                                label="45 min"
                                checked={duration === 45}
                                onChange={() => setDuration(45)}
                            />
                            <CustomFormInput
                                type="radio"
                                variant="turquoise"
                                layout="pill"
                                name="duration"
                                value="60"
                                label="60 min"
                                checked={duration === 60}
                                onChange={() => setDuration(60)}
                            />
                            <CustomFormInput
                                type="radio"
                                variant="turquoise"
                                layout="pill"
                                name="duration"
                                value="90"
                                label="90 min"
                                checked={duration === 90}
                                onChange={() => setDuration(90)}
                            />
                            <CustomFormInput
                                type="radio"
                                variant="turquoise"
                                layout="pill"
                                name="duration"
                                value="120"
                                label="120 min"
                                checked={duration === 120}
                                onChange={() => setDuration(120)}
                            />
                        </div>
                    </div>

                    {/* Schedule period. Always visible on create: a fresh schedule has no
                        dates yet, so there is nothing to summarise (the edit modal keeps the
                        collapsed from → to accordion because its dates already exist).
                        Divider above separates it from Lesson Duration. */}
                    <div className="form-group weekly-section-divided">
                        <label className="form-label">Schedule period</label>
                        {/* Start date → Duration → End date, all live-synced on one row.
                            Edit the weeks and the end date recomputes; edit the end date
                            and the weeks round to the nearest whole week. */}
                        {!startDate && (
                            <p className="form-hint date-duration-hint">Pick a start date to set the duration and end date.</p>
                        )}
                        <div className="date-duration-row">
                            <div className="form-group">
                                <label className="form-label">Start date</label>
                                <DateInputWithPicker
                                    value={startDate}
                                    onChange={handleStartDateChange}
                                />
                            </div>
                            <Icon path={mdiArrowRight} size={0.9} className="date-duration-arrow" color="var(--dl-color-turquoise-hover)" />
                            <div className="duration-block">
                                <span className="form-label">Duration</span>
                                <div className="duration-weeks-row">
                                    <input
                                        type="number"
                                        min="1"
                                        value={numWeeks}
                                        onChange={(e) => handleWeeksChange(parseInt(e.target.value))}
                                        className="weeks-number-input"
                                        disabled={!startDate}
                                    />
                                    <span className="weeks-suffix">weeks</span>
                                </div>
                            </div>
                            <Icon path={mdiArrowRight} size={0.9} className="date-duration-arrow" color="var(--dl-color-turquoise-hover)" />
                            <div className="form-group">
                                <label className="form-label">End date</label>
                                <DateInputWithPicker
                                    value={endDate}
                                    onChange={handleEndDateChange}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Weekly availability — one row per day (Google-calendar style),
                        shared with EditWeeklyScheduleModal via WeeklyDayRows. */}
                    <div className="form-group">
                        <label className="form-label">General availability</label>
                        <WeeklyDayRows dayConfig={dayConfig} setDayConfig={setDayConfig} duration={duration} />
                    </div>

                    {/* Publish total — per-day granularity lives in the row glyphs/counts */}
                    {affectedDates.length > 0 && (
                        <div className="preview-summary">
                            <div className="preview-total">
                                <strong>Total: {affectedDates.length} dates, {affectedDates.reduce((sum, dateObj) => {
                                    const dayName = DAY_NAMES[dateObj.weekday === 7 ? 0 : dateObj.weekday];
                                    return sum + computeDayGlyphItems(dayConfig[dayName], duration)
                                        .filter(i => i.type === 'slot').length;
                                }, 0)} bookable slots</strong>
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="weekly-schedule-modal-footer">
                    <button className="btn-cancel" onClick={onCancel} disabled={isCreating}>
                        Cancel
                    </button>
                    <button className="btn-primary" onClick={handlePublish} disabled={isCreating}>
                        {isCreating ? 'Publishing...' : 'Publish Schedule'}
                    </button>
                </div>
            </div>

        </div>
    );
};


export default WeeklyScheduleModal;
